import React, { useState, useEffect } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    DialogContent,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import DraggableDialog from '../../../ui/draggableDialog';
import { toast } from 'react-toastify';
import { isLocalDev } from '../../../../utils/environment';
import { LensConfigurationService, ValidDataService } from '../../../../service/validDataService';
import { initialValuesLensModelForm } from './validLensModelInputForm';

export default function EditValidLensModelForm({ open, onClose, onRecordEdit, recordToEdit, isCopy = false }) {
    const [manufacturers, setManufacturers] = useState([]);
    const [brands, setBrands] = useState([]);

    useEffect(() => {
        if (open) {
            ValidDataService.getManufacturersByType('lens').then(result => {
                const { code, data } = result;
                if (code === 0) {
                    setManufacturers(data || []);
                }
            }).catch(error => {
                if (isLocalDev) console.error(error);
            });

            if (recordToEdit?.manufacturerId) {
                ValidDataService.getManufacturerBrands(recordToEdit.manufacturerId, 'lenses').then(result => {
                    const { code, data } = result;
                    if (code === 0) {
                        setBrands(data || []);
                    }
                }).catch(error => {
                    if (isLocalDev) console.error(error);
                });
            }
        }
    }, [open, recordToEdit]);

    const handleManufacturerChange = async (manufacturerId, setFieldValue) => {
        setFieldValue('manufacturerId', manufacturerId);
        setFieldValue('manufacturerBrandId', '');
        setBrands([]);

        if (manufacturerId) {
            try {
                const result = await ValidDataService.getManufacturerBrands(manufacturerId, 'lenses');
                const { code, data } = result;
                if (code === 0) {
                    setBrands(data || []);
                }
            } catch (error) {
                if (isLocalDev) console.error(error);
            }
        }
    };

    const handleSubmit = async (values, { resetForm }) => {
        try {
            let response;

            if (isCopy) {
                const { id, ...newValues } = values;
                response = await LensConfigurationService.addModel(newValues);
            } else {
                response = await LensConfigurationService.updateModel(values);
            }
            
            const { code, data, message } = response;
            if (code === 0) {
                toast.success(message);
                onClose();
                onRecordEdit(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.log(error);
            }
            toast.error('Failed to update lens model');
        }
    };

    return (
        <DraggableDialog title={isCopy ? "Copy Lens Model" : "Edit Lens Model"} onClose={onClose} open={open} width={'sm'}>
            <DialogContent>
                <Formik
                    initialValues={{ ...initialValuesLensModelForm, ...recordToEdit }}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, values, setFieldValue, handleChange, handleBlur, touched, errors }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small" error={touched.manufacturerId && !!errors.manufacturerId}>
                                        <InputLabel required>Manufacturer</InputLabel>
                                        <Select
                                            name="manufacturerId"
                                            value={values.manufacturerId}
                                            onChange={(e) => handleManufacturerChange(e.target.value, setFieldValue)}
                                            onBlur={handleBlur}
                                            label="Manufacturer"
                                        >
                                            {manufacturers.map((mfr) => (
                                                <MenuItem key={mfr.id} value={mfr.id}>
                                                    {mfr.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {touched.manufacturerId && errors.manufacturerId && (
                                            <FormHelperText>{errors.manufacturerId}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small" disabled={!values.manufacturerId}>
                                        <InputLabel>Brand (Optional)</InputLabel>
                                        <Select
                                            name="manufacturerBrandId"
                                            value={values.manufacturerBrandId || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Brand (Optional)"
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            {brands.map((brand) => (
                                                <MenuItem key={brand.id} value={brand.id}>
                                                    {brand.brandName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Lens Type</InputLabel>
                                        <Select
                                            name="lensType"
                                            value={values.lensType || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Lens Type"
                                        >
                                            <MenuItem value="">None</MenuItem>
                                            <MenuItem value="single_vision">Single Vision</MenuItem>
                                            <MenuItem value="bifocal">Bifocal</MenuItem>
                                            <MenuItem value="trifocal">Trifocal</MenuItem>
                                            <MenuItem value="progressive">Progressive</MenuItem>
                                            <MenuItem value="aphakic">Aphakic</MenuItem>
                                            <MenuItem value="vocational">Vocational</MenuItem>
                                        </Select>
                                        <FormHelperText>The design type of this lens model</FormHelperText>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="modelName"
                                        label="Model Name"
                                        required
                                        fullWidth
                                        size="small"
                                    />
                                    <ErrorMessage name="modelName">{message => <Typography color={'red'}>{message}</Typography>}</ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={2}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        {isCopy ? 'Create Copy' : 'Update'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </DialogContent>
        </DraggableDialog>
    );
};